import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import './LoginPage.css'; // Custom CSS for additional styling

export default function MyOrders() {
    const [activeTab, setActiveTab] = useState('active');
    const [orders, setOrders] = useState([]);
    const [pendingOrders, setPendingOrders] = useState([]);
    const [completedOrders, setCompletedOrders] = useState([]);
    const [editData, setEditData] = useState({ target: '', stoploss: '', trdid: '' });
    const [isLoading, setIsLoading] = useState(true);

    // Fetch data based on active tab
    useEffect(() => {
        const userid = localStorage.getItem('userid');
        setIsLoading(true);

        const fetchData = async () => {
            try {
                let url = '';
                if (activeTab === 'active') {
                    url = `https://wealthmcx.com/Rest5/my_trades_api/${userid}`;
                } else {
                    url = `https://wealthmcx.com/Rest5/exit_trades_api/${userid}`;
                }

                const response = await fetch(url);
                const data = await response.json();
                
                if (activeTab === 'active') setOrders(data);
                else if (activeTab === 'pending') setPendingOrders(data);
                else setCompletedOrders(data);
                
                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching orders:", error);
                setIsLoading(false);
            }
        };

        const interval = setInterval(fetchData, 1000);
        fetchData(); // Initial fetch

        return () => clearInterval(interval);
    }, [activeTab]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditData(prev => ({ ...prev, [name]: value }));
    };

    const handleSquareOff = (tradeId) => {
        confirmAlert({
            title: 'Confirm Square Off',
            message: 'Are you sure you want to square off this trade?',
            buttons: [
                {
                    label: 'Yes',
                    onClick: () => executeSquareOff(tradeId)
                },
                {
                    label: 'No',
                    onClick: () => {}
                }
            ]
        });
    };

    const executeSquareOff = async (tradeId) => {
        try {
            const response = await fetch(`https://wealthmcx.com/Rest5/square_off_api/${tradeId}`);
            const result = await response.json();
            alert(result.msg);
        } catch (error) {
            console.error("Error squaring off trade:", error);
            alert("Failed to square off trade");
        }
    };

    const handleEditTrade = async (tradeId) => {
        try {
            const response = await fetch(`https://wealthmcx.com/Rest5/edit_trade_api/${tradeId}`);
            const data = await response.json();
            setEditData({ target: data.target, stoploss: data.stoploss, trdid: tradeId });
        } catch (error) {
            console.error("Error fetching trade details:", error);
        }
    };

    const handleUpdateTrade = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('https://wealthmcx.com/Rest5/web_update_trade_api/', {
                target: editData.target,
                stoploss: editData.stoploss,
                bsid: editData.trdid
            });
            alert(response.data.msg);
            // Close modal or refresh data
        } catch (error) {
            console.error("Error updating trade:", error);
            alert("Failed to update trade");
        }
    };

    const toggleDeliveryMode = async (tradeId, currentMode) => {
        const action = currentMode === 'On' ? 'intraday' : 'delivery';
        const url = `https://wealthmcx.com/Rest5/set_${action}_api/${tradeId}`;
        
        try {
            await fetch(url);
            // Data will refresh automatically due to the interval
        } catch (error) {
            console.error(`Error setting ${action} mode:`, error);
        }
    };

    const renderOrderCard = (order, isActive = false) => {
        const profitLossColor = order.pro_loss > 0 ? 'success' : 'danger';
        const profitLossSign = order.pro_loss > 0 ? '+' : '';
        
        return (
            <div className="order-card" key={order.bsid}>
                <div className="order-header">
                    <h3 className="order-asset text-white">{order.metal}</h3>
                    <span className={`order-type ${order.type.toLowerCase()}`}>
                        {order.type} {order.lotsize}  Lot
                    </span>

                </div>
                
                <div className="order-details">
                    <div className="detail-group">
                        <span className="detail-label">Target</span>
                        <span className="detail-value">{order.target}</span>
                    </div>
                    <div className="detail-group">
                        <span className="detail-label">LMT - LTP</span>
                        <span className="detail-value">{order.setlimit} - {order.last_live}</span>
                    </div>
                    
                    
                    <div className="detail-group">
                        <span className="detail-label">Stoploss</span>
                        <span className="detail-value">{order.stoploss}</span>
                    </div>
                    
                    <div className="detail-group">
                        <span className="detail-label">P/L</span>
                        <span className={`detail-value ${profitLossColor}`}>
                            {profitLossSign}{order.pro_loss}
                        </span>
                    </div>
                </div>
                
                {isActive && (
                    <div className="order-actions">
                        {order.sq_off !== 'On' && order.holding !== 'On' && (
                            <button 
                                className="action-btn square-off"
                                onClick={() => handleSquareOff(order.bsid)}
                            >
                                Square Off
                            </button>
                        )}
                        
                        <button 
                            className="action-btn edit"
                            onClick={() => handleEditTrade(order.bsid)}
                            data-bs-toggle="modal" 
                            data-bs-target="#editTradeModal"
                        >
                            Edit
                        </button>
                        
                        <button 
                            className={`action-btn delivery ${order.holding === 'On' ? 'active' : ''}`}
                            onClick={() => toggleDeliveryMode(order.bsid, order.holding)}
                        >
                            {order.holding === 'On' ? 'Delivery' : 'Intraday'}
                            {order.holding === 'On' && <span className="badge">HOLD</span>}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="orders-dashboard">
            {/* Header */}

            
            {/* Tabs */}
            <div className="orders-tabs">
                <button 
                    className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
                    onClick={() => setActiveTab('active')}
                >
                    Active Trades
                </button>
               
                <button 
                    className={`tab-btn ${activeTab === 'completed' ? 'active' : ''}`}
                    onClick={() => setActiveTab('completed')}
                >
                    Closed Trades
                </button>
            </div>
            
            {/* Loading State */}
            {isLoading && (
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading orders...</p>
                </div>
            )}
            
            {/* Orders List */}
            {!isLoading && (
                <div className="orders-list">
                    {activeTab === 'active' && orders.map(order => renderOrderCard(order, true))}
                    {activeTab === 'completed' && completedOrders.map(renderOrderCard)}
                    
                    {(activeTab === 'active' && orders.length === 0) && (
                        <div className="empty-state">
                            <i className="fa fa-box-open"></i>
                            <p>No active trades found</p>
                        </div>
                    )}
                    
                    {(activeTab === 'pending' && pendingOrders.length === 0) && (
                        <div className="empty-state">
                            <i className="fa fa-clock"></i>
                            <p>No pending orders found</p>
                        </div>
                    )}
                    
                    {(activeTab === 'completed' && completedOrders.length === 0) && (
                        <div className="empty-state">
                            <i className="fa fa-check-circle"></i>
                            <p>No Closed trades found</p>
                        </div>
                    )}
                </div>
            )}
            
            {/* Edit Trade Modal */}
            <div className="modal fade" id="editTradeModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Trade</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleUpdateTrade}>
                                <div className="form-group">
                                    <label>Target Price</label>
                                    <input
                                        type="number"
                                        name="target"
                                        value={editData.target}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        required
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Stoploss Price</label>
                                    <input
                                        type="number"
                                        name="stoploss"
                                        value={editData.stoploss}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        required
                                    />
                                </div>
                                
                                <button type="submit" className="btn btn-primary btn-block">
                                    Update Trade
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}